import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

export default function Terms() {
  return (
    <div className="min-h-screen bg-bone-100" data-testid="terms-page">
      <header
        className="bg-bone-100/75 backdrop-blur-xl border-b border-bone-300 sticky top-0 z-10"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-brand inline-block"></span> Hush
          </Link>
          <Link to="/" className="text-sm text-ink-500 hover:text-ink-900 flex items-center gap-2" data-testid="terms-back">
            <ArrowLeft size={16} /> Back home
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
        <p className="eyebrow">Last updated: May 12, 2026</p>
        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-3 mb-10">Terms of Service</h1>

        <Section title="Acceptance">
          By using Hush you agree to these terms and our <Link to="/privacy" className="text-brand underline">Privacy Policy</Link>. If you don't agree, please don't use the app.
        </Section>

        <Section title="Your account">
          You're responsible for keeping your password (or third-party login) secure. You must be at
          least 13 years old to use Hush. One person, one account.
        </Section>

        <Section title="Your content">
          Anything you record, transcribe, or save in Hush is <b>yours</b>. We claim no ownership over
          your journal entries. By using the service you grant us the minimum license required to
          process, store, and display them back to you (and to feed them to our AI providers as
          described in the Privacy Policy).
        </Section>

        <Section title="Acceptable use">
          Don't try to break the service, reverse-engineer it, scrape it, or use it to harm others.
          Don't upload content that's illegal where you live.
        </Section>

        <Section title="Subscriptions & billing">
          Hush is free to start. Some features (unlimited entries, full history, AI weekly insights,
          search, export) require Hush Plus, billed monthly or annually through Apple App Store or
          Google Play. All purchases are subject to those stores' standard refund policies.
          Subscriptions auto-renew until you cancel them in your store account.
        </Section>

        <Section title="Service availability">
          We do our best to keep Hush running smoothly, but we don't guarantee 100% uptime. We may
          change, suspend, or discontinue features with reasonable notice.
        </Section>

        <Section title="Disclaimers">
          Hush is a journaling and self-reflection tool. It is <b>NOT</b> a substitute for professional
          mental-health care, medical advice, or therapy. If you are in crisis, please reach out to a
          qualified professional or a local emergency service.
        </Section>

        <Section title="Limitation of liability">
          To the maximum extent permitted by law, Hush is provided "as is" without warranty. Our
          aggregate liability for any claim related to the service is limited to the greater of $50
          USD or what you paid us in the prior 12 months.
        </Section>

        <Section title="Termination">
          You can stop using Hush at any time and delete your account from Settings. We can suspend
          or terminate accounts that violate these terms.
        </Section>

        <Section title="Governing law">
          These terms are governed by the laws of your country of residence (and where required, the
          jurisdiction in which we operate the service).
        </Section>

        <Section title="Contact">
          Reach us at <a href="mailto:help@apphush.online" className="text-brand underline">help@apphush.online</a>.
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-serif text-2xl text-ink-900 mb-3">{title}</h2>
      <div className="text-ink-700 leading-relaxed text-[15px]">{children}</div>
    </section>
  );
}
